// The supplier bid grid (Lane 1331, Phase 2 slice 2B; new look Lane 1394,
// mockup v2): a spreadsheet-style table with one row per part and one line per
// cell. It only draws rows and reports edits; the screen (BidScreen.js) owns
// the draft, the saving and the rules.
//
// Columns: part number, description (the buyer's notes show on hover), unit,
// the volume (one column per site when the buyer shares sites), the read-only
// "Round N-1 price" (round 2 on: the supplier's own price as it stood when this
// round opened), the "Round N price" input, minimum order, lead time, No bid,
// and a note icon that opens the per-part note.
//
// A bad cell has a red outline and says why on hover; the screen says how many
// rows need a fix under the grid.
//
// Keyboard: Tab moves across a row (the inputs are in reading order); Enter
// moves to the same column in the next row (Shift+Enter: the row above).
import React, { useRef } from 'react';
import {
  formatVolume, partsShowSites, buyerNotesText, siteColumns, siteQuantity,
} from './portalView';
import { validateRow, rowNeedsAnswer, LEAD_MAX } from './bidModel';

const NEEDS_ANSWER = 'Add a price or mark No bid, or this part is not sent.';

function NoteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 5h16v11H9l-5 4z" />
      <path d="M8 9h8M8 12h5" />
    </svg>
  );
}

// `previousOf(pn)`: { text, gone } for the read-only previous-round column
// (`gone`: why that price no longer counts, shown struck through), or null.
// `keepsOf(pn)`: true when an empty price keeps an offer from an earlier round.
export default function BidGrid({
  items, round, showPrevious, rowOf, previousOf, keepsOf, serverErrorOf, isDirty, canEdit,
  onChange, onLeave, onNote, needsAnswerOf,
}) {
  const tableRef = useRef(null);
  const showSites = partsShowSites(items);
  const sites = showSites ? siteColumns(items) : [];

  const onKeyDown = (e) => {
    if (e.key !== 'Enter' || e.target.tagName !== 'INPUT') return;
    const { row, col } = e.target.dataset;
    if (row === undefined) return;
    e.preventDefault();
    const next = tableRef.current && tableRef.current.querySelector(
      `[data-row="${Number(row) + (e.shiftKey ? -1 : 1)}"][data-col="${col}"]`);
    if (next) next.focus();
  };

  // Leaving the grid (focus moves outside it) saves at once.
  const onBlur = (e) => {
    if (tableRef.current && !tableRef.current.contains(e.relatedTarget)) onLeave();
  };

  return (
    <div className="srcp-scroll" ref={tableRef} onKeyDown={onKeyDown} onBlur={onBlur}>
      <table className="srcp-grid srcp-bid">
        <thead>
          <tr>
            <th className="srcp-sticky">Part number</th><th>Description</th><th>Unit</th>
            {showSites
              ? sites.map((site) => <th key={site} className="srcp-r">{site} per year</th>)
              : <th className="srcp-r">Annual volume</th>}
            {showPrevious && <th className="srcp-r">Round {round - 1} price</th>}
            <th className="srcp-r">Round {round} price</th>
            <th className="srcp-r">Minimum order</th>
            <th className="srcp-r">Lead time (days)</th>
            <th>No bid</th>
            <th><span className="srcp-sr">Note</span></th>
          </tr>
        </thead>
        <tbody>
          {items.map((p, i) => {
            const pn = p.part_number;
            const row = rowOf(pn);
            const errors = isDirty(pn) ? validateRow(row) : {};
            const serverError = serverErrorOf(pn);
            const bad = Object.keys(errors).length > 0 || Boolean(serverError);
            const notes = buyerNotesText(p, showSites);
            const tip = notes ? `${p.description || pn}. Buyer note: ${notes}` : (p.description || '');
            const prev = showPrevious ? previousOf(pn) : null;
            // The price cell carries a row-level problem from the server, and
            // the "add a price" hint when the row has details but no answer.
            const priceProblem = errors.price || (!errors.lead && !errors.moq && serverError) || '';
            // Lane 1440: a carried offer's details alone need no answer (needsAnswerOf).
            const needsAnswer = needsAnswerOf ? needsAnswerOf(pn) : rowNeedsAnswer(row);
            const priceTitle = priceProblem || (canEdit && needsAnswer ? NEEDS_ANSWER : undefined);
            const cell = (col, label, problem) => ({
              'data-row': i,
              'data-col': col,
              disabled: !canEdit,
              'aria-label': `${label} for ${pn}`,
              'aria-invalid': problem ? true : undefined,
              title: problem || undefined,
            });
            const inputClass = (problem, short) => `srcp-cell-input${short ? ' srcp-cell-input--short' : ''}${problem ? ' srcp-cell-input--bad' : ''}`;
            const hasNote = Boolean(row.note.trim());
            return (
              <tr key={pn} className={bad ? 'srcp-row-bad' : undefined}>
                <td className="srcp-sticky srcp-mono">{pn}</td>
                <td className="srcp-ell" title={tip}>{p.description || ''}</td>
                <td>{p.unit || ''}</td>
                {showSites
                  ? sites.map((site) => <td key={site} className="srcp-r srcp-num">{siteQuantity(p, site)}</td>)
                  : <td className="srcp-r srcp-num">{formatVolume(p.volume)}</td>}
                {showPrevious && (
                  <td className={`srcp-r srcp-num${prev && prev.gone ? ' srcp-gone' : ''}`}
                    title={prev && prev.gone ? prev.gone : undefined}>{prev ? prev.text : ''}</td>
                )}
                <td className="srcp-r srcp-cellwrap">
                  <input className={inputClass(priceProblem)} inputMode="decimal" autoComplete="off"
                    value={row.price} placeholder={keepsOf(pn) ? 'Keep' : ''}
                    {...cell('price', `Round ${round} price`, priceProblem)} title={priceTitle}
                    disabled={!canEdit || row.no_bid}
                    onChange={(e) => onChange(pn, 'price', e.target.value)} />
                </td>
                <td className="srcp-r srcp-cellwrap">
                  <input className={inputClass(errors.moq, true)} inputMode="numeric" autoComplete="off"
                    value={row.moq} {...cell('moq', 'Minimum order', errors.moq)}
                    onChange={(e) => onChange(pn, 'moq', e.target.value)} />
                </td>
                <td className="srcp-r srcp-cellwrap">
                  <input className={inputClass(errors.lead, true)} inputMode="numeric" autoComplete="off"
                    value={row.lead} {...cell('lead', `Lead time in days (0 to ${LEAD_MAX})`, errors.lead)}
                    onChange={(e) => onChange(pn, 'lead', e.target.value)} />
                </td>
                <td className="srcp-cellwrap">
                  <input type="checkbox" className="srcp-check" checked={row.no_bid} {...cell('no_bid', 'No bid')}
                    onChange={(e) => onChange(pn, 'no_bid', e.target.checked)} />
                </td>
                <td className="srcp-cellwrap">
                  <button type="button" data-row={i} data-col="note"
                    className={`srcp-icon-btn${hasNote ? ' srcp-icon-btn--on' : ''}${errors.note ? ' srcp-icon-btn--bad' : ''}`}
                    disabled={!canEdit && !hasNote}
                    title={errors.note || (hasNote ? `Your note: ${row.note}` : 'Add a note for this part')}
                    aria-label={hasNote ? `Your note for ${pn}` : `Add a note for ${pn}`}
                    onClick={() => onNote(pn)}>
                    <NoteIcon />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
